import { useNavigate } from 'react-router-dom';
import { Smartphone, Eye, Bug } from 'lucide-react';
import { formatRelativeTime } from '@/utils/formatters';

interface DeviceCardProps {
  id: string;
  name: string;
  user: string;
  status: 'online' | 'offline';
  platform: 'iOS' | 'Android';
  appVersion: string;
  osVersion: string;
  lastActivity: string;
  sessions: number;
  errors: number;
  crashes: number;
}

export function DeviceCard({
  name,
  user,
  status,
  platform: _platform,
  appVersion,
  osVersion,
  lastActivity,
  sessions,
  errors,
  crashes,
  id,
}: DeviceCardProps) {
  const navigate = useNavigate();
  const isOnline = status === 'online';

  const handleLogsClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigate(`/logs?device=${id}`);
  };

  const handleErrorsClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigate(`/errors?device=${id}`);
  };

  return (
    <div className="group flex cursor-pointer flex-col justify-between rounded-lg border border-border bg-card p-5 shadow-sm transition-all hover:-translate-y-1 hover:shadow-lg">
      <div className="flex flex-col gap-4">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            {/* Status Indicator */}
            <div className="relative flex h-3 w-3">
              {isOnline ? (
                <>
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-online opacity-75"></span>
                  <span className="relative inline-flex h-3 w-3 rounded-full bg-online"></span>
                </>
              ) : (
                <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-offline"></span>
              )}
            </div>
            <div className="flex flex-col">
              <h3 className="font-semibold text-text-title">{name}</h3>
              <p className="text-xs text-text-secondary">Usuário: {user}</p>
            </div>
          </div>
          <Smartphone size={24} className="text-text-secondary" />
        </div>

        {/* Device Info */}
        <div className="space-y-2 text-xs text-text-secondary">
          <p className="truncate font-mono">
            ID: <span className="text-text-title">{id}</span>
          </p>
          <p>
            App: <span className="font-medium text-text-title">{appVersion}</span> | OS:{' '}
            <span className="font-medium text-text-title">{osVersion}</span>
          </p>
          <p>
            Última atividade:{' '}
            <span className="font-medium text-text-title">{formatRelativeTime(lastActivity)}</span>
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-3 divide-x divide-border rounded-lg bg-muted/50 p-2 text-center">
          <div className="px-1">
            <p className="text-sm font-bold text-text-title">{sessions.toLocaleString()}</p>
            <p className="text-xs text-text-secondary">Sessões</p>
          </div>
          <div className="px-1">
            <p className="text-sm font-bold text-text-title">{errors}</p>
            <p className="text-xs text-text-secondary">Erros</p>
          </div>
          <div className="px-1">
            <p className="text-sm font-bold text-text-title">{crashes}</p>
            <p className="text-xs text-text-secondary">Crashes</p>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="mt-5 flex justify-between gap-2 border-t border-border pt-4">
        <button
          onClick={handleLogsClick}
          className="flex flex-1 items-center justify-center gap-2 rounded-md py-2 text-xs font-semibold text-text-secondary hover:bg-muted"
        >
          <Eye size={16} /> Logs
        </button>
        <button
          onClick={handleErrorsClick}
          className="flex flex-1 items-center justify-center gap-2 rounded-md py-2 text-xs font-semibold text-text-secondary hover:bg-muted"
        >
          <Bug size={16} /> Erros
        </button>
      </div>
    </div>
  );
}
